<template>
  <generic-modal
    :title="$t('personal savings account request')"
    ref="generic_modal"
    size="fullscreen"
  >
    <div v-if="request.uuid">
      <generic-tabs
        :tabs="[
          {
            name: 'tab1',
            label: $t('request information'),
            icon: 'fa fa-info',
          },
          {
            name: 'tab2',
            label: $t('captured information'),
            icon: 'fa fa-check',
          },
          { name: 'tab3', label: $t('documentation'), icon: 'fa fa-file' },
          { name: 'tab4', label: $t('onfido'), icon: 'fa fa-shield' },
        ]"
        orientation="horizontal"
      >
        <template v-slot:tab1>
          <!-- Request Information -->
          <div class="box mb-4">
            <h4 class="title is-5 has-text-primary mb-3">
              {{ $t("request information") }}
            </h4>
            <div class="columns is-multiline">
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("destinatary") }}:</label>
                  <p class="content">
                    {{ request.destinatary?.name || $t("not available") }}
                  </p>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("status") }}:</label>
                  <span class="tag is-info">{{ $t(request.status) }}</span>
                  <p class="content">{{ formatDate(request.create_date) }}</p>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("product") }}:</label>
                  <p class="content">
                    {{ request.product?.name || $t("not available") }}
                  </p>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("entity") }}:</label>
                  <p class="content">
                    {{ request.entity?.name || $t("not available") }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Settings Information -->
          <div v-if="request.settings" class="box mb-4">
            <h4 class="title is-5 has-text-primary mb-3">
              {{ $t("configuration") }}
            </h4>
            <div class="columns is-multiline">
              <!-- Flow Configuration -->
              <div class="column is-12" v-if="request.settings.flow">
                <div class="field">
                  <label class="label is-small">{{ $t("flow") }}:</label>
                  <span class="tag is-primary">
                    {{ $t(request.settings.flow) }}
                  </span>
                </div>
              </div>

              <!-- Communication Settings -->
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("send sms") }}:</label>
                  <span
                    :class="[
                      'tag',
                      request.settings.send_sms ? 'is-success' : 'is-danger',
                    ]"
                  >
                    {{ getBooleanLabel(request.settings.send_sms) }}
                  </span>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("send email") }}:</label>
                  <span
                    :class="[
                      'tag',
                      request.settings.send_email ? 'is-success' : 'is-danger',
                    ]"
                  >
                    {{ getBooleanLabel(request.settings.send_email) }}
                  </span>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small"
                    >{{ $t("send whatsapp") }}:</label
                  >
                  <span
                    :class="[
                      'tag',
                      request.settings.send_whatsapp
                        ? 'is-success'
                        : 'is-danger',
                    ]"
                  >
                    {{ getBooleanLabel(request.settings.send_whatsapp) }}
                  </span>
                </div>
              </div>

              <!-- Verification Settings -->
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small">{{ $t("run onfido") }}:</label>
                  <span
                    :class="[
                      'tag',
                      request.settings.run_onfido ? 'is-success' : 'is-danger',
                    ]"
                  >
                    {{ getBooleanLabel(request.settings.run_onfido) }}
                  </span>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small"
                    >{{ $t("reproduce video") }}:</label
                  >
                  <span
                    :class="[
                      'tag',
                      request.settings.reproduce_video
                        ? 'is-success'
                        : 'is-danger',
                    ]"
                  >
                    {{ getBooleanLabel(request.settings.reproduce_video) }}
                  </span>
                </div>
              </div>
              <div class="column is-6">
                <div class="field">
                  <label class="label is-small"
                    >{{ $t("security method") }}:</label
                  >
                  <span class="tag is-info">
                    {{
                      $t(request.settings.security_method) ||
                      $t("not specified")
                    }}
                  </span>
                </div>
              </div>

              <!-- Documentation Requirements -->
              <div v-if="request.settings.documentation" class="column is-12">
                <div class="field">
                  <label class="label is-small"
                    >{{ $t("required documentation") }}:</label
                  >
                  <div class="tags">
                    <span
                      v-for="(doc, index) in request.settings.documentation"
                      :key="index"
                      :class="[
                        'tag',
                        doc.is_required ? 'is-warning' : 'is-info',
                      ]"
                    >
                      {{ doc.name }}
                      <span v-if="doc.is_required" class="ml-1">*</span>
                    </span>
                  </div>
                  <p class="help">
                    <span class="tag is-warning is-small">*</span>
                    {{ $t("required") }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:tab2>
          <!-- Captured Data -->
          <div v-if="request.captured_data" class="box">
            <h4 class="title is-5 has-text-primary mb-4">
              {{ $t("captured information") }}
            </h4>

            <!-- 1. Identity Information -->
            <div v-if="request.captured_data.identity" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("identity information") }}
                </h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("first name") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.identity.first_name ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("second name") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.identity.second_name ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("first surname") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.identity.first_surname ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("second surname") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.identity.second_surname ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("cui dpi") }}:</label>
                    <p class="content">
                      {{
                        request.captured_data.identity.cui ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("birth date") }}:</label
                    >
                    <p class="content">
                      {{
                        formatDate(request.captured_data.identity.birth_date) ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("nationality") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.identity.nationality ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("has other nationality") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.identity.has_other_nationality
                        )
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 2. Acting Type Information -->
            <div v-if="request.captured_data.acting_type" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("acting type information") }}
                </h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("gender") }}:</label>
                    <p class="content">
                      {{
                        getGenderLabel(request.captured_data.acting_type.gender)
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("marital status") }}:</label
                    >
                    <p class="content">
                      {{
                        getMaritalStatusLabel(
                          request.captured_data.acting_type.marital_status
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("acts own behalf") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.acting_type.acts_own_behalf
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("department according to dpi") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.acting_type.department_dpi ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("municipality according to dpi") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.acting_type.municipality_dpi ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-12">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("profession occupation") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.acting_type
                          .profession_occupation || $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 3. Contact and Address Information -->
            <div v-if="request.captured_data.contact_address" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">{{ $t("contact and address") }}</h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("cellphone 1") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.contact_address.cellphone_1 ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("email") }}:</label>
                    <p class="content">
                      {{
                        request.captured_data.contact_address.email ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("home zone") }}:</label>
                    <p class="content">
                      {{
                        request.captured_data.contact_address.home_zone ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("home country") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.contact_address.home_country ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("home department") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.contact_address.home_department ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("home department id") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.contact_address
                          .home_department_id || $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-12">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("home address") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.contact_address.home_address ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 4. Economic Information -->
            <div v-if="request.captured_data.economic_info" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("economic information") }}
                </h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-12">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("economic activity according to rtu") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info
                          .economic_activity_rtu || $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("income sources") }}:</label
                    >
                    <p class="content">
                      {{
                        getIncomeSourceLabel(
                          request.captured_data.economic_info.income_sources
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("seniority years") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info.seniority_years ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("monthly income amount") }}:</label
                    >
                    <p class="content">
                      {{
                        formatCurrency(
                          request.captured_data.economic_info
                            .monthly_income_amount
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("monthly expenses amount") }}:</label
                    >
                    <p class="content">
                      {{
                        formatCurrency(
                          request.captured_data.economic_info
                            .monthly_expenses_amount
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("business commercial name") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info
                          .business_commercial_name || $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("employer name") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info.employer_name ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("dependency work") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info.dependency_work ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    request.captured_data.economic_info.other_income_specify
                  "
                  class="column is-6"
                >
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("other income specify") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.economic_info.other_income_specify
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    request.captured_data.economic_info.income_sources_selection
                  "
                  class="column is-12"
                >
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("income sources selection") }}:</label
                    >
                    <div class="tags">
                      <span
                        v-for="source in request.captured_data.economic_info
                          .income_sources_selection"
                        :key="source"
                        class="tag is-info"
                      >
                        {{ getIncomeSourceLabel(source) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- 5. Transactional Profile -->
            <div
              v-if="request.captured_data.transactional_profile"
              class="mb-5"
            >
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("transactional profile") }}
                </h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("account purpose") }}:</label
                    >
                    <p class="content">
                      {{
                        getAccountPurposeLabel(
                          request.captured_data.transactional_profile
                            .account_purpose
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("funds source") }}:</label
                    >
                    <p class="content">
                      {{
                        getFundsSourceLabel(
                          request.captured_data.transactional_profile
                            .funds_source
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    request.captured_data.transactional_profile
                      .funds_source_other_specify
                  "
                  class="column is-6"
                >
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("funds source other specify") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.transactional_profile
                          .funds_source_other_specify
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="
                    request.captured_data.transactional_profile
                      .account_purpose_other_specify
                  "
                  class="column is-6"
                >
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("account purpose other specify") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.transactional_profile
                          .account_purpose_other_specify
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 6. Beneficiaries -->
            <div v-if="request.captured_data.beneficiaries_info" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("beneficiaries information") }}
                </h5>
              </div>
              <div class="field">
                <label class="label is-small"
                  >{{ $t("has beneficiaries") }}:</label
                >
                <p class="content">
                  {{
                    getBooleanLabel(
                      request.captured_data.beneficiaries_info.has_beneficiaries
                    )
                  }}
                </p>
              </div>
              <div
                v-if="
                  request.captured_data.beneficiaries_info.beneficiaries &&
                  hasValidBeneficiaries(
                    request.captured_data.beneficiaries_info.beneficiaries
                  )
                "
              >
                <div class="table-container">
                  <table class="table is-fullwidth is-striped">
                    <thead>
                      <tr>
                        <th>{{ $t("beneficiary name") }}</th>
                        <th>{{ $t("dpi id") }}</th>
                        <th>{{ $t("relationship") }}</th>
                        <th>{{ $t("percentage") }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(beneficiary, index) in request.captured_data
                          .beneficiaries_info.beneficiaries"
                        :key="index"
                      >
                        <td
                          v-if="
                            beneficiary.name ||
                            beneficiary.dpi_id ||
                            beneficiary.relationship ||
                            beneficiary.percentage
                          "
                        >
                          {{ beneficiary.name || $t("not specified") }}
                        </td>
                        <td
                          v-if="
                            beneficiary.name ||
                            beneficiary.dpi_id ||
                            beneficiary.relationship ||
                            beneficiary.percentage
                          "
                        >
                          {{ beneficiary.dpi_id || $t("not specified") }}
                        </td>
                        <td
                          v-if="
                            beneficiary.name ||
                            beneficiary.dpi_id ||
                            beneficiary.relationship ||
                            beneficiary.percentage
                          "
                        >
                          {{ beneficiary.relationship || $t("not specified") }}
                        </td>
                        <td
                          v-if="
                            beneficiary.name ||
                            beneficiary.dpi_id ||
                            beneficiary.relationship ||
                            beneficiary.percentage
                          "
                        >
                          {{ beneficiary.percentage || $t("not specified") }}%
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- 7. Compliance -->
            <div v-if="request.captured_data.compliance" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-0">
                  {{ $t("compliance information") }}
                </h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("is politically exposed person") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(request.captured_data.compliance.is_pep)
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("is state contractor") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.compliance.is_state_contractor
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div
                  v-if="request.captured_data.compliance.pep_specify"
                  class="column is-12"
                >
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("pep specification") }}:</label
                    >
                    <p class="content">
                      {{ request.captured_data.compliance.pep_specify }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 8. FATCA Information -->
            <div v-if="request.captured_data.fatca" class="mb-5">
              <div class="notification is-dark">
                <h5 class="title is-6 mb-3">{{ $t("fatca information") }}</h5>
              </div>
              <div class="columns is-multiline">
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("born in usa") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(request.captured_data.fatca.born_in_usa)
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("us citizen or green card") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.fatca.us_citizen_green_card
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("us tax resident") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.fatca.us_tax_resident
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("us address") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(request.captured_data.fatca.us_address)
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("us phone") }}:</label>
                    <p class="content">
                      {{
                        getBooleanLabel(request.captured_data.fatca.us_phone)
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("us transfer instructions") }}:</label
                    >
                    <p class="content">
                      {{
                        getBooleanLabel(
                          request.captured_data.fatca.us_transfer_instructions
                        )
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small">{{ $t("tin ssn") }}:</label>
                    <p class="content">
                      {{
                        request.captured_data.fatca.tin_ssn ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
                <div class="column is-6">
                  <div class="field">
                    <label class="label is-small"
                      >{{ $t("document w9 w8ben") }}:</label
                    >
                    <p class="content">
                      {{
                        request.captured_data.fatca.document_w9_w8ben ||
                        $t("not specified")
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Veracity Confirmation -->
            <div
              v-if="request.captured_data.confirm_veracity !== undefined"
              class="mb-5"
            >
              <div class="notification is-dark">
                <h5 class="title is-6 mb-3">
                  {{ $t("veracity confirmation") }}
                </h5>
              </div>
              <div class="field">
                <label class="label is-small"
                  >{{ $t("information veracity confirmed") }}:</label
                >
                <span
                  :class="[
                    'tag',
                    request.captured_data.confirm_veracity
                      ? 'is-success'
                      : 'is-warning',
                  ]"
                >
                  {{
                    request.captured_data.confirm_veracity
                      ? $t("yes")
                      : $t("no")
                  }}
                </span>
              </div>
            </div>
          </div>
          <div v-else class="notification is-info">
            <div class="has-text-centered">
              <span class="icon is-large">
                <i class="fa fa-info-circle fa-2x"></i>
              </span>
              <p class="mt-2">{{ $t("no captured information available") }}</p>
              <p class="is-size-7 has-text-grey">
                {{ $t("data capture has not been completed yet") }}
              </p>
            </div>
          </div>
        </template>

        <template v-slot:tab3>
          <!-- Documentation Section -->
          <div
            v-if="request.documentation && request.documentation.length > 0"
            class="box"
          >
            <h4 class="title is-5 has-text-primary mb-4">
              {{ $t("uploaded documentation") }}
            </h4>
            <div class="columns is-multiline">
              <div
                v-for="(doc, index) in request.documentation"
                :key="index"
                class="column is-6"
              >
                <div class="card">
                  <div class="card-header">
                    <div class="card-header-title">
                      <span class="icon has-text-success mr-2">
                        <i class="fa fa-file-o"></i>
                      </span>
                      <span>{{ doc.document_type }}</span>
                    </div>
                  </div>
                  <div class="card-content">
                    <div class="media">
                      <div class="media-left">
                        <figure class="image is-48x48">
                          <span class="icon is-large has-text-info">
                            <i
                              :class="getDocumentIcon(doc.file)"
                              class="fa fa-2x"
                            ></i>
                          </span>
                        </figure>
                      </div>
                      <div class="media-content">
                        <p class="title is-6">{{ doc.mime_type }}</p>
                        <p class="subtitle is-7 has-text-grey">
                          {{ $t("document type") }}:
                          {{ translate_file_size(doc.size) }}
                        </p>
                      </div>
                    </div>

                    <!-- Document Preview -->
                    <div class="document-preview mb-3">
                      <!-- Image Preview -->
                      <div v-if="isImageFile(doc.file)" class="image-preview">
                        <figure class="image">
                          <img
                            :src="getDocumentUrl(doc.file)"
                            :alt="doc.document_type"
                            class="preview-image"
                            @error="onImageError"
                          />
                        </figure>
                      </div>

                      <!-- PDF Preview -->
                      <div v-else-if="isPdfFile(doc.file)" class="pdf-preview">
                        <iframe
                          :src="getDocumentUrl(doc.file)"
                          class="pdf-iframe"
                          frameborder="0"
                        ></iframe>
                      </div>

                      <!-- Other file types - show icon and info -->
                      <div v-else class="file-info-preview">
                        <div class="has-text-centered py-4">
                          <span class="icon is-large has-text-grey-light">
                            <i
                              :class="getDocumentIcon(doc.file)"
                              class="fa fa-4x"
                            ></i>
                          </span>
                          <p class="has-text-grey mt-2">
                            {{ $t("preview not available for this file type") }}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div class="content">
                      <div class="buttons is-centered">
                        <button
                          class="button is-small is-success"
                          @click="downloadDocument(doc)"
                          :title="$t('download document')"
                        >
                          <span class="icon">
                            <i class="fa fa-download"></i>
                          </span>
                          <span>{{ $t("download") }}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="notification is-info">
            <div class="has-text-centered">
              <span class="icon is-large">
                <i class="fa fa-file-o fa-2x"></i>
              </span>
              <p class="mt-2">{{ $t("no documentation available") }}</p>
              <p class="is-size-7 has-text-grey">
                {{ $t("no documents have been uploaded yet") }}
              </p>
            </div>
          </div>
        </template>

        <template v-slot:tab4>
          <!-- Onfido Verification Results -->
          <div v-if="request.onfido_request" class="box">
            <h4 class="title is-5 has-text-primary mb-4">
              <span class="icon">
                <i class="fa fa-shield"></i>
              </span>
              {{ $t("onfido verification results") }}
            </h4>

            <!-- Check Results - Detailed Verification Reports -->
            <div
              v-if="
                request.onfido_request.check_result &&
                request.onfido_request.check_result.length > 0
              "
            >
              <div
                v-for="(report, index) in request.onfido_request.check_result"
                :key="report.id"
                class="mb-4"
              >
                <div class="card">
                  <div class="card-header">
                    <p class="card-header-title">
                      <span class="icon">
                        <i class="fa fa-certificate"></i>
                      </span>
                      {{ format_report_name(report.name) }} - {{ $t("report") }}
                    </p>
                    <div class="card-header-icon">
                      <span
                        class="tag"
                        :class="get_result_class(report.result)"
                      >
                        <span class="icon">
                          <i
                            class="fa"
                            :class="get_result_icon(report.result)"
                          ></i>
                        </span>
                        <span>{{ format_result(report.result) }}</span>
                      </span>
                    </div>
                  </div>
                  <div class="card-content">
                    <!-- Report Basic Info -->
                    <div class="columns">
                      <div class="column is-3">
                        <div class="field">
                          <label class="label is-small">{{
                            $t("report id")
                          }}</label>
                          <p class="is-size-7">{{ report.id }}</p>
                        </div>
                      </div>
                      <div class="column is-3">
                        <div class="field">
                          <label class="label is-small">{{
                            $t("status")
                          }}</label>
                          <span
                            class="tag"
                            :class="get_status_class_report(report.status)"
                          >
                            {{ format_check_status(report.status) }}
                          </span>
                        </div>
                      </div>
                      <div class="column is-3">
                        <div class="field">
                          <label class="label is-small">{{
                            $t("created at")
                          }}</label>
                          <p class="has-text-weight-semibold">
                            {{ formatDate(report.created_at) }}
                          </p>
                        </div>
                      </div>
                      <div class="column is-3" v-if="report.sub_result">
                        <div class="field">
                          <label class="label is-small">{{
                            $t("sub result")
                          }}</label>
                          <span
                            class="tag"
                            :class="get_result_class(report.sub_result)"
                          >
                            {{ format_result(report.sub_result) }}
                          </span>
                        </div>
                      </div>
                    </div>

                    <!-- Document Report Properties -->
                    <div
                      v-if="
                        report.properties &&
                        Object.keys(report.properties).length > 0
                      "
                      class="mt-4"
                    >
                      <h6 class="subtitle is-6">
                        <span class="icon">
                          <i class="fa fa-info-circle"></i>
                        </span>
                        {{ $t("extracted information") }}
                      </h6>
                      <div class="columns is-multiline">
                        <div
                          class="column is-4"
                          v-if="report.properties.first_name"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("first name")
                            }}</label>
                            <p class="has-text-weight-semibold">
                              {{ report.properties.first_name }}
                            </p>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.properties.last_name"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("last name")
                            }}</label>
                            <p class="has-text-weight-semibold">
                              {{ report.properties.last_name }}
                            </p>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.properties.date_of_birth"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("date of birth")
                            }}</label>
                            <p class="has-text-weight-semibold">
                              {{ report.properties.date_of_birth }}
                            </p>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.properties.gender"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("gender")
                            }}</label>
                            <span class="tag is-info">{{
                              report.properties.gender
                            }}</span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.properties.nationality"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("nationality")
                            }}</label>
                            <span class="tag is-success">{{
                              report.properties.nationality
                            }}</span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.properties.document_type"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("document type")
                            }}</label>
                            <span class="tag is-primary">{{
                              format_document_type(
                                report.properties.document_type
                              )
                            }}</span>
                          </div>
                        </div>
                        <div
                          class="column is-6"
                          v-if="report.properties.date_of_expiry"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("expiry date")
                            }}</label>
                            <p class="has-text-weight-semibold">
                              {{ report.properties.date_of_expiry }}
                            </p>
                          </div>
                        </div>
                        <div
                          class="column is-6"
                          v-if="report.properties.issuing_country"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("issuing country")
                            }}</label>
                            <span class="tag is-warning">{{
                              report.properties.issuing_country
                            }}</span>
                          </div>
                        </div>
                        <div
                          class="column is-12"
                          v-if="
                            report.properties.document_numbers &&
                            report.properties.document_numbers.length > 0
                          "
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("document numbers")
                            }}</label>
                            <div class="tags">
                              <span
                                v-for="docNum in report.properties
                                  .document_numbers"
                                :key="docNum.type"
                                class="tag is-dark"
                              >
                                {{ docNum.type }}: {{ docNum.value }}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Facial Similarity Score -->
                    <div
                      v-if="
                        report.name === 'facial_similarity_photo' &&
                        report.breakdown?.face_comparison?.breakdown?.face_match
                      "
                      class="mt-4"
                    >
                      <h6 class="subtitle is-6">
                        <span class="icon">
                          <i class="fa fa-user-circle"></i>
                        </span>
                        {{ $t("face comparison") }}
                      </h6>
                      <div class="columns">
                        <div class="column is-4">
                          <div class="field">
                            <label class="label is-small">{{
                              $t("similarity score")
                            }}</label>
                            <div class="control">
                              <span
                                class="tag is-large"
                                :class="
                                  get_score_class(
                                    report.breakdown.face_comparison.breakdown
                                      .face_match.properties.score
                                  )
                                "
                              >
                                <span class="icon">
                                  <i class="fa fa-percent"></i>
                                </span>
                                <span
                                  >{{
                                    (
                                      report.breakdown.face_comparison.breakdown
                                        .face_match.properties.score * 100
                                    ).toFixed(2)
                                  }}%</span
                                >
                              </span>
                            </div>
                          </div>
                        </div>
                        <div class="column is-4">
                          <div class="field">
                            <label class="label is-small">{{
                              $t("match result")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.face_comparison.breakdown
                                    .face_match.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.face_comparison.breakdown
                                        .face_match.result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.face_comparison.breakdown
                                    .face_match.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Breakdown Summary for Document Reports -->
                    <div
                      v-if="report.breakdown && report.name === 'document'"
                      class="mt-4"
                    >
                      <h6 class="subtitle is-6">
                        <span class="icon">
                          <i class="fa fa-check-square-o"></i>
                        </span>
                        {{ $t("verification breakdown") }}
                      </h6>
                      <div class="columns is-multiline">
                        <div
                          class="column is-4"
                          v-if="report.breakdown.data_validation"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("data validation")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.data_validation.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.data_validation.result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.data_validation.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.breakdown.visual_authenticity"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("visual authenticity")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.visual_authenticity.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.visual_authenticity
                                        .result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.visual_authenticity.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.breakdown.image_integrity"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("image integrity")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.image_integrity.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.image_integrity.result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.image_integrity.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.breakdown.data_consistency"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("data consistency")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.data_consistency.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.data_consistency.result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.data_consistency.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.breakdown.compromised_document"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("compromised document")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.compromised_document.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.compromised_document
                                        .result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.compromised_document.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                        <div
                          class="column is-4"
                          v-if="report.breakdown.age_validation"
                        >
                          <div class="field">
                            <label class="label is-small">{{
                              $t("age validation")
                            }}</label>
                            <span
                              class="tag"
                              :class="
                                get_result_class(
                                  report.breakdown.age_validation.result
                                )
                              "
                            >
                              <span class="icon">
                                <i
                                  class="fa"
                                  :class="
                                    get_result_icon(
                                      report.breakdown.age_validation.result
                                    )
                                  "
                                ></i>
                              </span>
                              <span>{{
                                format_result(
                                  report.breakdown.age_validation.result
                                )
                              }}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="notification is-info is-dark">
              <p class="has-text-centered">
                <span class="icon">
                  <i class="fa fa-info-circle"></i>
                </span>
                {{ $t("no verification results available") }}
              </p>
              <div class="buttons is-centered mt-3">
                <div
                  class="button is-small is-warning"
                  @click="initiateOnfidoCheck(request.uuid)"
                >
                  <span class="icon">
                    <i class="fa fa-shield"></i>
                  </span>
                  <span>{{ $t("initiate onfido verification") }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="notification is-info">
            <div class="has-text-centered">
              <span class="icon is-large">
                <i class="fa fa-shield fa-2x"></i>
              </span>
              <p class="mt-2">
                {{ $t("no onfido verification results available") }}
              </p>
              <p class="is-size-7 has-text-grey">
                {{
                  $t(
                    "onfido verification has not been initiated or completed yet"
                  )
                }}
              </p>
            </div>
          </div>
        </template>
      </generic-tabs>
    </div>

    <div v-else class="has-text-centered p-5">
      <div class="notification is-info is-dark">
        <p>{{ $t("loading request information") }}...</p>
      </div>
    </div>

    <template v-slot:footer>
      <button class="button" @click="close_modal">
        <span class="icon">
          <i class="fa fa-times"></i>
        </span>
        <span>{{ $t("close") }}</span>
      </button>
    </template>
  </generic-modal>
</template>

<script>
import GenericTabs from "@/components/generic_tabs/Index.vue";
import GenericModal from "@/components/generic_modal/Index.vue";
import {
  post,
  put,
  get,
  success_message,
  error_message,
  translate_file_size,
  drive_static_file_url,
} from "@/utils";
import moment from "moment";

export default {
  name: "PersonalSavingsRequestViewModal",
  components: { GenericModal, GenericTabs },
  data() {
    return {
      request: {},
      error: "",
    };
  },
  computed: {},
  methods: {
    translate_file_size,
    show(uuid) {
      let v = this;
      v.$refs.generic_modal.show_modal();
      get("/account-request/personal-savings", { uuid: uuid })
        .then((response) => {
          v.request = response;
        })
        .catch((error) => {
          v.error = error_message(error);
        });
    },
    formatDate(dateString) {
      if (!dateString) return this.$t("not specified");
      return moment(dateString).format("DD/MM/YYYY");
    },
    formatCurrency(amount) {
      if (!amount || amount === "") return this.$t("not specified");
      return `Q ${parseFloat(amount).toLocaleString("es-GT", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    getBooleanLabel(value) {
      if (value === "yes" || value === true) return this.$t("yes");
      if (value === "no" || value === false) return this.$t("no");
      return this.$t("not specified");
    },
    getGenderLabel(gender) {
      if (gender === "M") return this.$t("male");
      if (gender === "F") return this.$t("female");
      return this.$t("not specified");
    },
    getMaritalStatusLabel(status) {
      const statusMap = {
        S: this.$t("single"),
        M: this.$t("married"),
        D: this.$t("divorced"),
        W: this.$t("widowed"),
        U: this.$t("common law"),
      };
      return statusMap[status] || this.$t("not specified");
    },
    getIncomeSourceLabel(source) {
      const sourceMap = {
        own_business: this.$t("own business"),
        professional_activities: this.$t("professional activities"),
        dependency_relationship: this.$t("dependency relationship"),
        retirement: this.$t("retirement"),
        maintenance: this.$t("maintenance"),
        income: this.$t("income"),
        other_specify: this.$t("other specify"),
      };
      return sourceMap[source] || this.$t("not specified");
    },
    getAccountPurposeLabel(purpose) {
      const purposeMap = {
        transfers: this.$t("transfers"),
        savings: this.$t("savings"),
        payroll: this.$t("payroll"),
        others: this.$t("others"),
      };
      return purposeMap[purpose] || this.$t("not specified");
    },
    getFundsSourceLabel(source) {
      const sourceMap = {
        fees: this.$t("fees"),
        salary: this.$t("salary"),
        sales: this.$t("sales"),
        remittances: this.$t("remittances"),
        investment: this.$t("investment"),
        others: this.$t("others"),
      };
      return sourceMap[source] || this.$t("not specified");
    },
    hasValidBeneficiaries(beneficiaries) {
      if (!beneficiaries || !Array.isArray(beneficiaries)) return false;
      return beneficiaries.some(
        (b) => b.name || b.dpi_id || b.relationship || b.percentage
      );
    },
    // Documentation methods
    getDocumentIcon(filename) {
      if (!filename) return "fa-file-o";
      const extension = filename.toLowerCase().split(".").pop();
      switch (extension) {
        case "pdf":
          return "fa-file-pdf-o";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
          return "fa-file-image-o";
        case "doc":
        case "docx":
          return "fa-file-word-o";
        case "xls":
        case "xlsx":
          return "fa-file-excel-o";
        default:
          return "fa-file-o";
      }
    },
    getFileName(filepath) {
      if (!filepath) return this.$t("unknown file");
      return filepath.split("/").pop() || this.$t("unknown file");
    },
    getDocumentUrl(filepath) {
      if (!filepath) return "";
      return drive_static_file_url(filepath);
    },
    isImageFile(filename) {
      if (!filename) return false;
      const extension = filename.toLowerCase().split(".").pop();
      return ["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension);
    },
    isPdfFile(filename) {
      if (!filename) return false;
      const extension = filename.toLowerCase().split(".").pop();
      return extension === "pdf";
    },
    onImageError(event) {
      console.error("Error loading image:", event);
      // Could show a placeholder or error message
    },
    downloadDocument(doc) {
      if (!doc.file) {
        error_message(this.$t("file not available"));
        return;
      }
      const fileUrl = drive_static_file_url(doc.file);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = this.getFileName(doc.file);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    // Onfido verification result methods
    format_report_name(name) {
      if (!name) return "N/A";
      return name.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    format_result(result) {
      if (!result) return "N/A";
      return result.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    format_check_status(status) {
      if (!status) return "N/A";
      return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    format_document_type(type) {
      if (!type) return "N/A";
      return type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    },
    get_result_class(result) {
      switch (result) {
        case "clear":
          return "is-success";
        case "consider":
          return "is-warning";
        case "unidentified":
          return "is-info";
        case "caution":
          return "is-warning";
        case "suspected":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    get_result_icon(result) {
      switch (result) {
        case "clear":
          return "fa-check-circle";
        case "consider":
          return "fa-exclamation-triangle";
        case "unidentified":
          return "fa-question-circle";
        case "caution":
          return "fa-warning";
        case "suspected":
          return "fa-times-circle";
        default:
          return "fa-question-circle";
      }
    },
    get_status_class_report(status) {
      switch (status) {
        case "complete":
          return "is-success";
        case "in_progress":
          return "is-warning";
        case "awaiting_data":
          return "is-info";
        case "failed":
          return "is-danger";
        default:
          return "is-light";
      }
    },
    get_score_class(score) {
      if (score >= 0.8) return "is-success";
      if (score >= 0.6) return "is-warning";
      return "is-danger";
    },
    close_modal() {
      let v = this;
      v.$refs.generic_modal.close_modal();
      v.set_initial_state();
    },
    set_initial_state() {
      this.request = {};
      this.error = "";
    },
    initiateOnfidoCheck(uuid) {
      let v = this;
      if (!uuid) {
        error_message(v.$t("invalid request identifier"));
        return;
      }
      post("/account-request/personal-savings/update-onfido", { uuid: uuid })
        .then((response) => {
          success_message(v.$t("onfido verification initiated successfully"));
          // Refresh request data to get updated verification status
          return get("/account-request/personal-savings", { uuid: uuid });
        })
        .then((response) => {
          v.request = response;
        })
        .catch((error) => {
          error_message(error);
        });
    },
  },
};
</script>

<style scoped>
/* Documentation styling */
.card .media .image {
  display: flex;
  align-items: center;
  justify-content: center;
}

.card .buttons {
  margin-top: 0.5rem;
}

.card .buttons .button {
  margin: 0.25rem;
}

/* Document preview styles */
.document-preview {
  border: 1px solid #e1e1e1;
  border-radius: 6px;
  overflow: hidden;
  background: #fafafa;
}

.image-preview {
  max-height: 300px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-preview .image {
  margin: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 4px;
}

.pdf-preview {
  height: 300px;
  position: relative;
}

.pdf-iframe {
  width: 100%;
  height: 100%;
  border: none;
}

.file-info-preview {
  min-height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f8f8;
}

/* Responsive adjustments */
@media screen and (max-width: 768px) {
  .column.is-6 {
    padding: 0.5rem;
  }

  .card-content {
    padding: 1rem;
  }

  .buttons .button {
    font-size: 0.75rem;
  }

  .image-preview,
  .pdf-preview {
    max-height: 200px;
  }

  .preview-image {
    max-height: 200px;
  }

  .pdf-iframe {
    height: 200px;
  }

  .file-info-preview {
    min-height: 120px;
  }
}
</style>
